import { useState } from 'react';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Busy } from '@/components/kit';
import { cn } from '@/lib/utils';
import { useT } from '@/shared/i18n';

/** A button that asks before running a consequential, non-delete action. Return false to keep it open. */
export function ConfirmAction({
  title,
  description,
  onConfirm,
  label,
  icon,
  confirmLabel,
  busyLabel = 'Working…',
  variant = 'outline',
  size = 'sm',
  destructive = false,
  disabled = false,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { t } = useT();

  async function go(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const ok = await onConfirm();
      if (ok !== false) setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant={variant}
          size={size}
          disabled={disabled}
          className={cn(destructive && variant !== 'destructive' && 'text-destructive hover:text-destructive', className)}
        >
          {icon && <ApperIcon name={icon} size={16} />}
          {label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{t('Keep it')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={go}
            disabled={busy}
            className={cn(destructive && 'bg-destructive text-destructive-foreground hover:bg-destructive/90')}
          >
            <Busy busy={busy} label={confirmLabel || label} busyLabel={busyLabel} />
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
